import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Breadcrumb } from '@app/models/breadcrumb.model';
import { ZendeskToken, ZendeskTokenServerSide } from '@app/models/integrations/zendesk/zendesk-token.model';
import { IState } from '@app/models/state/state.model';
import { ZendeskAPIService } from '@app/shared/api/zendesk.api.service';
import { BreadcrumbService } from '@app/shared/breadcrumbs/breadcrumbs.service';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { PillType } from '@app/shared/components/pill/pill.component';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { SwalUtils } from '@app/shared/utils/swal.utils';
import { environment } from 'environments/environment';

interface PageState extends IState {
  isConfiguring: boolean;

  submitted: boolean;
  submitting: boolean;
}

// enum WorkflowState {
//   INITIAL,
//   SETUP,
//   SYNCING,
//   COMPLETE,
//   UPDATE_API_KEY,
//   UPDATE_REFRESH_TOKEN,
// }

@Component({
  selector: 'app-integration-zendesk',
  templateUrl: './integration-zendesk.component.html',
  styleUrls: ['./integration-zendesk.component.scss'],
})
export class IntegrationZendeskComponent implements OnInit, OnDestroy {
  // public readonly eWorkflowState = WorkflowState;
  public readonly eButtonType = ButtonType;
  public readonly ePillType = PillType;
  public readonly clientId = environment.zendesk.clientId;
  public readonly redirectUri = environment.zendesk.redirectUri;

  state: PageState;
  breadcrumb: Breadcrumb;
  
  integrationSetupForm: FormGroup;
  currentToken: ZendeskToken | null;

  // TODO: Remove or rework this
  zendeskError: string | null;

  get controlTenantId(): FormControl {
    return this.integrationSetupForm.controls.tenantId as FormControl;
  }

  constructor(
    public route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private zendeskAPIService: ZendeskAPIService,
    private swalUtils: SwalUtils,
    private notifyUtils: NotifyUtils
  ) {
    this.currentToken = undefined;
    this.state = {
      loading: true,
      error: false,
      errorMessage: '',
      isConfiguring: false,
      submitted: false,
      submitting: false
    };

    this.breadcrumb = this.breadcrumbService.init(this.route);
    this.integrationSetupForm = this.initFormSetup();

    this.zendeskError = localStorage.getItem('zendesk_error');
  }

  ngOnInit(): void {
    this.getData();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  getData(): void {
    this.checkForZendeskError();
    this.zendeskAPIService.getToken()
      .toPromise()
      .then(token => {
        this.populatePageFromToken(token);
      })
      .finally(() => {
        this.state.loading = false;
      });
  }

  initFormSetup(token?: ZendeskToken): FormGroup {
    const formGroup = new FormGroup({
      tenantId: new FormControl('', [ Validators.required, Validators.maxLength(2000), Validators.pattern(/^[\w-]+.zendesk\.com$/) ]) //Validators.pattern(/^(?!http:\/\/|https:\/\/).\S{1,}(?<=\.zendesk\.com)$/)
    });
    
    if (token) {
      formGroup.controls.tenantId.setValue(token.tenantId);
    }

    return formGroup;
  }

  saveCredentials(): void {
    this.state.submitted = true;

    if (this.state.submitting) { return console.error('Form is already submitting'); }
    if (this.integrationSetupForm.invalid) { return console.error('Form is invalid'); }
    this.state.submitting = true;

    const updateRequest = {
      tenantId: this.controlTenantId.value.trim(),
    } as ZendeskTokenServerSide;

    this.zendeskAPIService.createToken(updateRequest)
      .toPromise()
      .then(token => {
        this.populatePageFromToken(token);
        this.state.submitted = false;
        this.state.isConfiguring = false;
      })
      .finally(() => {
        this.state.submitting = false;
      });
  }

  populatePageFromToken(token: ZendeskToken): void {
    this.integrationSetupForm = this.initFormSetup(token);
    this.currentToken = token;
  }

  startSetup(): void {
    this.state.isConfiguring = true;
  }

  cancelSetup(): void {
    this.state.isConfiguring = false;
  }
  
  removeToken(): void {
    if (this.state.submitting) { return; }
    this.state.submitting = true;

    this.zendeskAPIService.removeToken()
      .toPromise()
      .then(() => {
        this.getData();
      })
      .finally(() => {
        this.state.submitting = false;
      });
  }

  checkForZendeskError() {
    if (this.zendeskError) {
      const timestamp = +this.zendeskError.split(':')[0];
      const message = this.zendeskError.split(':')[1];
      // Check if error is 30 seconds old or less
      if (Date.now() - timestamp < 30 * 1000) {
        this.swalUtils.displayErrorSwal({
          title: 'Error',
          text: message,
          confirmButtonColor: '#2DA1B3',
        });
      }
      localStorage.removeItem('zendesk_error');
    }
  }

  connectToZendesk() {
    const url = `https://${this.currentToken?.tenantId}/oauth/authorizations/new?response_type=code&redirect_uri=${this.redirectUri}&client_id=${this.clientId}&scope=read&state=STATE`;
    window.open(url, '_blank');
  }

  runManualSync(): void {
    if (!this.currentToken) { return; }
    if (this.state.submitting) { return; }
    this.state.submitting = true;

    this.zendeskAPIService.runManualSync()
      .toPromise()
      .then(() => {
        this.currentToken.syncing = true;
        this.notifyUtils.notify('Manual sync started');
        // After 2 minutes, refresh the page
        setTimeout(() => {
          this.state.submitting = false;
          this.state.loading = true;
          this.getData();
        }, 120000);
      });
  }
}
